import { Injectable } from '@angular/core';
import { HoursOption } from '../models/hours-option';

export const REGISTRATION_STEPS = [
  'registration',
  'registration-phase',
  'registration-worker-select',
  'registration-worker-hour',
  'registration-worker-tool',
  'registration-drivers-select',
  'registration-drivers-kilometers',
  'registration-tools-select',
  'registration-tools-hour',
  'registration-controls',
  'registration-material',
  'client-signing',
  'tecnician-signing',
] as const;

export type RegistrationStep = typeof REGISTRATION_STEPS[number];

export interface WorkflowContext {
  options: HoursOption | undefined;
  siteManagerMode: boolean;
  hasControls: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationWorkflowService {

  private isStepActive(step: RegistrationStep, ctx: WorkflowContext): boolean {
    switch (step) {
      case 'registration':
        return true;
      case 'registration-phase':
        return !!ctx.options?.multiplePhases;
      case 'registration-worker-select':
        return ctx.siteManagerMode;
      case 'registration-worker-hour':
        return ctx.siteManagerMode;
      case 'registration-worker-tool':
        return !ctx.options?.driversRegistrationsDisable && !!ctx.options?.driversRegistrationsTool;
      case 'registration-drivers-select':
        return !ctx.options?.driversRegistrationsDisable && !ctx.options?.driversRegistrationsTool;
      case 'registration-drivers-kilometers':
        return !ctx.options?.driversRegistrationsDisable && !ctx.options?.driversRegistrationsTool;
      case 'registration-tools-select':
        return !!ctx.options?.toolRegistrationsEnabled;
      case 'registration-tools-hour':
        return !!ctx.options?.toolRegistrationsEnabled;
      case 'registration-controls':
        return ctx.hasControls;
      case 'registration-material':
        return !!ctx.options?.materialEnabled;
      case 'client-signing':
        return !!ctx.options?.signatureEnabled;
      case 'tecnician-signing':
        return !!ctx.options?.signatureEnabled;
      default:
        return false;
    }
  }

  getActiveSteps(ctx: WorkflowContext): RegistrationStep[] {
    return REGISTRATION_STEPS.filter(step => this.isStepActive(step, ctx));
  }

  getNextStep(currentStep: RegistrationStep, ctx: WorkflowContext): RegistrationStep | null {
    const allSteps = REGISTRATION_STEPS;
    const currentIndex = allSteps.indexOf(currentStep);
    if (currentIndex === -1) return null;

    for (let i = currentIndex + 1; i < allSteps.length; i++) {
      if (this.isStepActive(allSteps[i], ctx)) {
        return allSteps[i];
      }
    }
    return null;
  }

  getPreviousStep(currentStep: RegistrationStep, ctx: WorkflowContext): RegistrationStep | null {
    const allSteps = REGISTRATION_STEPS;
    const currentIndex = allSteps.indexOf(currentStep);
    if (currentIndex === -1) return null;

    for (let i = currentIndex - 1; i >= 0; i--) {
      if (this.isStepActive(allSteps[i], ctx)) {
        return allSteps[i];
      }
    }
    return null;
  }

  isFinalStep(currentStep: RegistrationStep, ctx: WorkflowContext): boolean {
    return this.getNextStep(currentStep, ctx) === null;
  }
}
